/**
 * Config object persisted to localStorage, under the key `dams.config`,
 * when the application is updated.
 */
export const localStorageConfig = {
    general: {
        documentTypesSupportingPublishing: ['StillImage', 'Fotografi']
    },
    mainSearch: {
        filterDrawerOpen: false,
        numRows: 100,
        displayType: "grid",
        sort: {
            column: "createdAt",
            order: "desc",
        },
        columns: {
            "mrt-row-select": true,
            "fileSize": true,
            "fileType": true,
            "title": true,
            "description": true,
            "collectionId": true,
            "customIdentifier": true,
            "productionDate": true,
            "documentType": true,
            "producer.reference.title": true,
            "persons.reference.title": true,
            "places.reference.name_and_title": true,
            "subjects": true,
            "license": true,
            "copyrightType": true,
            "createdByName": true,
            "remarks": true,
            "created_at": true,
            "updated_at": true,
            "mediae.reference.file_extension": true,
            "mediae.reference.fileSize": true,
            "copyrightClause": true,
            "reference_types": true,
            "folders": true,
            "mrt-row-spacer": true
        }
    },
    folderContentsSearch: {
        filterDrawerOpen: false,
        numRows: 100,
        displayType: "grid",
        sort: {
            column: "createdAt",
            order: "desc",
        },
        columns: {
            "mrt-row-select": true,
            "fileSize": true,
            "fileType": true,
            "title": true,
            "description": true,
            "collectionId": true,
            "customIdentifier": true,
            "productionDate": true,
            "documentType": true,
            "producer.reference.title": true,
            "persons.reference.title": true,
            "places.reference.name_and_title": true,
            "subjects": true,
            "license": true,
            "copyrightType": true,
            "createdByName": true,
            "remarks": true,
            "created_at": true,
            "updated_at": true,
            "mediae.reference.file_extension": true,
            "mediae.reference.fileSize": true,
            "copyrightClause": true,
            "reference_types": true,
            "folders": true,
            "mrt-row-spacer": true
        }
    },
    folderSearch: {
        filterDrawerOpen: false,
        numRows: 100,
        displayType: "grid",
        sort: {
            column: "title",
            order: "asc"
        },
        columns: {
            "mrt-row-select": true,
            "folderType": true,
            "title": true,
            "project-state": true,
            "description": true,
            "museumName": true,
            "createdByName": true,
            "created_at": true,
            "updated_at": true,
            "mrt-row-spacer": true
        }
    },
    folderListSearch: {
        filterDrawerOpen: false,
        numRows: 100,
        displayType: "grid",
        sort: {
            column: "createdAt",
            order: "desc"
        }
    },
    projectSearch: {
        filterDrawerOpen: false,
        numRows: 100,
        displayType: "table",
        columns: {
            "folderType": true,
            "title": true,
            "project-state": true,
            "description": true,
            "museumName": true,
            "createdByName": true,
            "created_at": true,
            "updated_at": true,
            "mrt-row-spacer": true
        },
        sort: {
            column: "createdAt",
            order: "desc"
        }
    },
    projectContentsSearch: {
        filterDrawerOpen: false,
        numRows: 100,
        displayType: "grid",
        sort: {
            column: "createdAt",
            order: "desc",
        },
        columns: {
            "mrt-row-select": true,
            "fileSize": true,
            "fileType": true,
            "title": true,
            "description": true,
            "collectionId": true,
            "customIdentifier": true,
            "productionDate": true,
            "documentType": true,
            "producer.reference.title": true,
            "persons.reference.title": true,
            "places.reference.name_and_title": true,
            "subjects": true,
            "license": true,
            "copyrightType": true,
            "createdByName": true,
            "remarks": true,
            "created_at": true,
            "updated_at": true,
            "mediae.reference.file_extension": true,
            "mediae.reference.fileSize": true,
            "copyrightClause": true,
            "reference_types": true,
            "folders": true,
            "mrt-row-spacer": true
        }
    },
    filters: {}
};


/**
 * Method used to get the list of columns stored in localStorage for the specific view.
 * If no columns are stored in localStorage, the default columns are returned.
 *
 * @param searchView
 * @param publishingTagsEnabled - whether publishing tags column should be included
 * @returns {*}
 */
export const getColumns = (searchView, publishingTagsEnabled = false) => {
    const config = {...JSON.parse(localStorage.getItem('dams'))} || {};
    let columns = {...config?.[searchView]?.columns} || {};

    if (!columns || Object.keys(columns).length === 0 || Array.isArray(columns)) {
        columns = {...localStorageConfig[searchView].columns};
    }

    if (!publishingTagsEnabled) {
        // Remove publishing tags columns if they are disabled.
        delete columns['publishingTags'];
        delete columns['publishingTags-ems'];
        delete columns['publishingTags-nb'];
    }

    if (searchView === 'folderSearch') {
        delete columns['mrt-row-select'];
        config[searchView] = {...config[searchView], columns: columns};
        localStorage.setItem('dams', JSON.stringify(config));
        console.warn('[DAMS][config] - added mrt-row-select to folderSearch columns');
    }
    return columns;
};

/**
 * Method used to get the config stored in localStorage for the specific view.
 * If no config is stored in localStorage, the default config is returned.
 *
 * @param searchView
 * @returns {*}
 */
export const getSearchViewConfig = (searchView) => {
    const config = JSON.parse(localStorage.getItem('dams')) || {};
    if (!config?.[searchView]) {
        // No config found for the view, populate with defaults.
        config[searchView] = localStorageConfig[searchView];
        localStorage.setItem('dams', JSON.stringify(config));
        console.warn('[DAMS][config] - no config found for view: ' + searchView + ', populated with defaults.');
    }
    return config?.[searchView];
};

/**
 * Method used to get the specific config. section, stored in localStorage.
 * @param section
 * @returns {*|{}}
 */
export const getSectionConfig = (section) => {
    const config = JSON.parse(localStorage.getItem('dams')) || {};
    return config?.[section] || {};
};

/**
 * Method used to check if localStorage contains deprecated config. keys.
 * @returns {boolean}
 */
export const hasDeprecatedConfigKeys = () => {
    try {
        const keys = Object.keys(localStorage);
        return keys.some(k => k.indexOf('dams.') > -1);
    } catch (e) {
        return false;
    }
};

/**
 * Method used to remove deprecated config. keys from localStorage.
 * These are the keys used in previous versions of DAMS, before merging all config. settings into a single object.
 * @returns {void}
 */
export const removeDeprecatedConfigKeys = () => {
    try {
        const keys = Object.keys(localStorage);
        keys.forEach(k => {
            if (k.indexOf('dams.') > -1) {
                localStorage.removeItem(k);
                console.warn('[DAMS][config] - removed deprecated: ', k);
            }
        });
    } catch (e) {
        // ignore
    }
};

/**
 * Method used to populate localStorage with default config. values.
 * @returns {void}
 */
export const populateConfigWithDefaults = () => {
    try {
        localStorage.setItem('dams', JSON.stringify(localStorageConfig));
    } catch (e) {
        console.error('[DAMS][config] - failed to populate config with defaults');
    }
};
